/**
 * CR114: Asset Studio Types
 * 
 * Types for visual assets (SVG motifs, patterns, starbursts) used by Theme Labs skins.
 */

export type AssetType = 
  | "starburst" 
  | "pattern" 
  | "motif_corner" 
  | "motif_diagonal" 
  | "motif_strip" 
  | "icon"
  | "watermark"
  | "ribbon";

export type TintMode = "none" | "fill" | "stroke" | "both";

export type AssetStatus = "active" | "archived";

export interface AppAsset {
  id: string;
  name: string;
  type: AssetType;
  /** @deprecated Use url instead. Kept for migration period only. */
  svg: string;
  /** CR201: Public URL for the asset in storage */
  url: string | null;
  /** CR201: MIME type of the asset file */
  mime_type: string | null;
  /** CR201: File size in bytes */
  size_bytes: number | null;
  is_tileable: boolean;
  default_tint: string | null;
  tint_mode: TintMode;
  tags: string[];
  status: AssetStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface CreateAssetInput {
  name: string;
  type: AssetType;
  svg: string;
  is_tileable?: boolean;
  default_tint?: string | null;
  tint_mode?: TintMode;
  tags?: string[];
}

export interface UpdateAssetInput {
  name?: string;
  type?: AssetType;
  svg?: string;
  is_tileable?: boolean;
  default_tint?: string | null;
  tint_mode?: TintMode;
  tags?: string[];
  status?: AssetStatus;
}

// Type labels for UI
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  starburst: "Starburst",
  pattern: "Pattern",
  motif_corner: "Corner Motif",
  motif_diagonal: "Diagonal Motif",
  motif_strip: "Strip/Border",
  icon: "Icon",
  watermark: "Watermark",
  ribbon: "Ribbon",
};

export const TINT_MODE_LABELS: Record<TintMode, string> = {
  none: "No Tinting",
  fill: "Fill Only",
  stroke: "Stroke Only",
  both: "Fill & Stroke",
};

// Placeholder SVGs for initial seeding
// CR142: Added "default" tag support for starburst assets
export const PLACEHOLDER_SVGS: Record<string, { name: string; type: AssetType; svg: string; tags: string[] }> = {
  starburst_gold: {
    name: "Gold Starburst",
    type: "starburst",
    svg: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#FFD700"/>
      <stop offset="50%" stop-color="#FFC107"/>
      <stop offset="100%" stop-color="#FF9800"/>
    </linearGradient>
  </defs>
  <polygon points="60,5 71,35 105,35 78,57 89,91 60,70 31,91 42,57 15,35 49,35" fill="url(#goldGradient)"/>
</svg>`,
    tags: ["default", "starburst", "gold", "value"],
  },
  starburst_basic: {
    name: "Basic Starburst",
    type: "starburst",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <polygon points="50,5 61,35 95,35 68,57 79,91 50,70 21,91 32,57 5,35 39,35" fill="currentColor" opacity="0.3"/>
</svg>`,
    tags: ["placeholder", "starburst"],
  },
  pattern_dots: {
    name: "Dot Pattern",
    type: "pattern",
    svg: `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
  <circle cx="10" cy="10" r="2" fill="currentColor" opacity="0.15"/>
</svg>`,
    tags: ["placeholder", "pattern", "tileable"],
  },
  motif_corner_leaf: {
    name: "Corner Leaf",
    type: "motif_corner",
    svg: `<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <path d="M5,45 Q25,25 45,5 Q35,25 25,35 Q15,40 5,45" fill="currentColor" opacity="0.2"/>
</svg>`,
    tags: ["placeholder", "corner", "organic"],
  },
  motif_strip_wave: {
    name: "Wave Strip",
    type: "motif_strip",
    svg: `<svg viewBox="0 0 100 20" xmlns="http://www.w3.org/2000/svg">
  <path d="M0,10 Q25,0 50,10 Q75,20 100,10" stroke="currentColor" fill="none" stroke-width="2" opacity="0.2"/>
</svg>`,
    tags: ["placeholder", "strip", "wave"],
  },
  pattern_grid: {
    name: "Grid Pattern",
    type: "pattern",
    svg: `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
  <line x1="0" y1="10" x2="20" y2="10" stroke="currentColor" stroke-width="0.5" opacity="0.1"/>
  <line x1="10" y1="0" x2="10" y2="20" stroke="currentColor" stroke-width="0.5" opacity="0.1"/>
</svg>`,
    tags: ["placeholder", "pattern", "grid", "tileable"],
  },
  motif_diagonal_rays: {
    name: "Diagonal Rays",
    type: "motif_diagonal",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <line x1="0" y1="0" x2="100" y2="100" stroke="currentColor" stroke-width="1" opacity="0.1"/>
  <line x1="20" y1="0" x2="100" y2="80" stroke="currentColor" stroke-width="1" opacity="0.08"/>
  <line x1="40" y1="0" x2="100" y2="60" stroke="currentColor" stroke-width="1" opacity="0.06"/>
</svg>`,
    tags: ["placeholder", "diagonal", "rays"],
  },
  // CR310/CR335: System Default Ribbon - rounded banner shape (for horizontal banners)
  ribbon_default: {
    name: "Ribbon — Default",
    type: "ribbon",
    svg: `<svg viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg">
  <path
    d="M10 20 Q 10 10 20 10 L 300 10 Q 310 10 310 20 L 310 60 Q 310 70 300 70 L 20 70 Q 10 70 10 60 Z"
    fill="currentColor"
  />
</svg>`,
    tags: ["default", "ribbon", "banner", "system"],
  },
  // CR337: Legacy Corner Ribbon — matches production clip-path polygon(100% 0, 100% 100%, 0 0)
  ribbon_legacy_corner: {
    name: "Legacy Corner Ribbon",
    type: "ribbon",
    svg: `<svg viewBox="0 0 144 144" xmlns="http://www.w3.org/2000/svg">
  <polygon points="144,0 144,144 0,0" fill="currentColor"/>
</svg>`,
    tags: ["placeholder", "ribbon", "corner", "legacy"],
  },
};

/**
 * Safely validate SVG string
 * Returns error message if invalid, null if valid
 */
export function validateSvg(svg: string): string | null {
  if (!svg.trim()) {
    return "SVG content is required";
  }
  
  // Check for opening SVG tag
  if (!/<svg[\s>]/i.test(svg)) {
    return "Content must be a valid SVG (missing <svg> tag)";
  }
  
  // Check for script tags (security)
  if (/<script/i.test(svg)) {
    return "SVG cannot contain <script> tags for security reasons";
  }
  
  // Check for foreignObject (can embed HTML)
  if (/<foreignObject/i.test(svg)) {
    return "SVG cannot contain <foreignObject> for security reasons";
  }
  
  // Check for event handlers
  if (/\bon\w+\s*=/i.test(svg)) {
    return "SVG cannot contain event handlers (onclick, onload, etc.)";
  }
  
  return null;
}

/**
 * Sanitize SVG by removing potentially dangerous elements
 */
export function sanitizeSvg(svg: string): string {
  let sanitized = svg;
  
  // Remove script tags
  sanitized = sanitized.replace(/<script[\s\S]*?<\/script>/gi, "");
  
  // Remove foreignObject
  sanitized = sanitized.replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, "");
  
  // Remove event handlers
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, "");
  
  return sanitized;
}
